import { useState, useRef, FormEvent } from 'react';
import { format } from 'date-fns';
import { STORAGE_KEY, YOUR_GOOGLE_APPS_SCRIPT_URL } from '../constants/event.ts';

export interface RSVPFormData {
  name: string;
  phone: string;
  guests: number;
  organization: string;
  invitationFrom: string;
  wishes: string;
}

interface UseRSVPFormReturn {
  formData: RSVPFormData;
  setFormData: React.Dispatch<React.SetStateAction<RSVPFormData>>;
  isSubmitting: boolean;
  isSubmitted: boolean;
  submitError: string | null;
  handleSubmit: (e: FormEvent) => Promise<void>;
}

const initialFormData: RSVPFormData = {
  name: '',
  phone: '',
  guests: 1,
  organization: '',
  invitationFrom: '',
  wishes: '',
};

export const useRSVPForm = (): UseRSVPFormReturn => {
  const [formData, setFormData] = useState<RSVPFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isSubmittingRef = useRef(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;

    setIsSubmitting(true);
    setSubmitError(null);
    isSubmittingRef.current = true;

    const entry = {
      type: 'rsvp',
      name: formData.name,
      phone: formData.phone,
      guests: formData.guests,
      organization: formData.organization,
      invitationFrom: formData.invitationFrom,
      wishes: formData.wishes,
      timestamp: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
    };

    try {
      await fetch(YOUR_GOOGLE_APPS_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      });

      const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...existing, entry]));

      setIsSubmitted(true);
      setFormData(initialFormData);
    } catch (error) {
      console.error('Error saving RSVP:', error);
      setSubmitError('Sorry, there was a problem submitting your RSVP. Please try again.');
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  return {
    formData,
    setFormData,
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
  };
};

export interface WishesFormData {
  name: string;
  message: string;
}

interface UseWishesFormReturn {
  wishesData: WishesFormData;
  setWishesData: React.Dispatch<React.SetStateAction<WishesFormData>>;
  isSubmitting: boolean;
  isSubmitted: boolean;
  submitError: string | null;
  handleWishesSubmit: (e: FormEvent) => Promise<void>;
}

const initialWishesData: WishesFormData = {
  name: '',
  message: '',
};

export const useWishesForm = (): UseWishesFormReturn => {
  const [wishesData, setWishesData] = useState<WishesFormData>(initialWishesData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isSubmittingRef = useRef(false);

  const handleWishesSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;

    setIsSubmitting(true);
    setSubmitError(null);
    isSubmittingRef.current = true;

    const entry = {
      type: 'wishes',
      name: wishesData.name,
      wishes: wishesData.message,
      timestamp: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
    };

    try {
      await fetch(YOUR_GOOGLE_APPS_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      });

      setIsSubmitted(true);
      setWishesData(initialWishesData);
    } catch (error) {
      console.error('Error saving wish:', error);
      setSubmitError('Sorry, there was a problem sending your wish. Please try again.');
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  return {
    wishesData,
    setWishesData,
    isSubmitting,
    isSubmitted,
    submitError,
    handleWishesSubmit,
  };
};
